<template>
  <div class="search-experience-root">
  <search-results-classic v-if="!unified" v-on:switch-search="useUnified"></search-results-classic>
  <div v-else class="search-results-page">
    <Loading class="search-results-frame" :isLoading="searching">
      <div class="search-results-header">
        <div class="search-hit-summary" :class="{ 'search-hit-summary-error': searchError }">
          <span>{{ summary }}</span>
          <search-experience-banner mode="unified" align="header" v-on:switch="useClassic"></search-experience-banner>
        </div>
        <v-tabs
          v-if="!searchError"
          v-model="currentTab"
          color="white"
          slider-color="primary"
          class="search-hit-tabs">
          <v-tab class="search-hit-tab" ripple>All</v-tab>
          <v-tab class="search-hit-tab" ripple>Cases</v-tab>
          <v-tab class="search-hit-tab" ripple>Documents</v-tab>
        </v-tabs>
      </div>
      <div class="search-results-body">
        <div class="search-results-inset">
        <div v-if="searchError" class="search-hit-error" role="alert">{{ searchError }}</div>
        <v-tabs-items v-if="!searchError" v-model="currentTab">
          <v-tab-item :transition="false" :reverse-transition="false">
            <search-hit-list :rows="allRows" :search-term="searchTerm" :format-date="formatDate" v-on:open-file="openFile"></search-hit-list>
          </v-tab-item>
          <v-tab-item :transition="false" :reverse-transition="false">
            <search-hit-list :rows="caseRows" :search-term="searchTerm" :format-date="formatDate" v-on:open-file="openFile"></search-hit-list>
          </v-tab-item>
          <v-tab-item :transition="false" :reverse-transition="false">
            <search-hit-list :rows="documentRows" :search-term="searchTerm" :format-date="formatDate" v-on:open-file="openFile"></search-hit-list>
          </v-tab-item>
        </v-tabs-items>
        <div v-if="!searchError && pageCount > 1" class="search-hit-pager">
          <v-btn flat small :disabled="page === 0" v-on:click="goToPage(page - 1)">Previous</v-btn>
          <span>Page {{ page + 1 }} of {{ pageCount }}</span>
          <v-btn flat small :disabled="page + 1 >= pageCount" v-on:click="goToPage(page + 1)">Next</v-btn>
        </div>
        </div>
      </div>
    </Loading>
  </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import Loading from "@/components/shared/Loading.vue"
import Helpers from "@/helpers.js"
import DataProvider from "@/components/buckets/bucket-providers/SearchDataProvider.js"
import SearchHitList from "@/components/search/SearchHitList.vue"
import SearchResultsClassic from "@/components/search/SearchResultsClassic.vue"
import SearchExperienceBanner from "@/components/search/SearchExperienceBanner.vue"
import { fieldLabel, rowsFromLegacy, summaryLine, totalsFromLegacy } from "@/components/search/unifiedSearch.js"
import Vue from "vue"

const PAGE_SIZE = 20

export default {
  name: "SearchResults",
  components: { Loading, SearchHitList, SearchResultsClassic, SearchExperienceBanner },
  props: {
    dataProvider: {
      type: Object,
      default: function() { return DataProvider }
    },
    helpers: {
      type: Object,
      default: function() { return Helpers }
    }
  },
  data: () => ({
    searching: false,
    currentTab: 0,
    page: 0,
    results: [],
    documentSearch: { documents: [], total: 0 },
    matches: null,
    caseMatches: null,
    documentMatches: null,
    matchTotal: null,
    caseMatchTotal: null,
    documentMatchTotal: null,
    searchError: ""
  }),
  computed: {
    ...mapState({
      searchTerm: state => state.cbroute.params.search_term,
      unifiedSearchPreference: state => state.currentUser.me.preferences.unifiedSearch
    }),
    unified() {
      return this.unifiedSearchPreference !== false
    },
    usesUnified() {
      return this.matches !== null && this.matches !== undefined
    },
    legacyRows() {
      return rowsFromLegacy(this.results, this.documentSearch)
    },
    allRows() {
      if (this.usesUnified) {
        return this.decorate(this.matches)
      }
      return this.decorate(this.legacyRows)
    },
    caseRows() {
      if (this.usesUnified) {
        return this.decorate(this.caseMatches || [])
      }
      return this.allRows.filter(function(row) { return row.kind === "case" })
    },
    documentRows() {
      if (this.usesUnified) {
        return this.decorate(this.documentMatches || [])
      }
      return this.allRows.filter(function(row) { return row.kind === "document" })
    },
    totals() {
      if (this.usesUnified) {
        return {
          matchTotal: this.matchTotal || 0,
          caseMatchTotal: this.caseMatchTotal || 0,
          documentMatchTotal: this.documentMatchTotal || 0
        }
      }
      return totalsFromLegacy(this.results, this.documentSearch)
    },
    activeFilter() {
      if (this.currentTab === 1) {
        return "cases"
      }
      if (this.currentTab === 2) {
        return "documents"
      }
      return "all"
    },
    summary() {
      if (this.searchError) {
        return "Search failed"
      }
      return summaryLine(this.activeFilter, this.totals)
    },
    activeTotal() {
      if (this.activeFilter === "cases") {
        return this.totals.caseMatchTotal
      }
      if (this.activeFilter === "documents") {
        return this.totals.documentMatchTotal
      }
      return this.totals.matchTotal
    },
    pageCount() {
      if (!this.activeTotal) {
        return 1
      }
      return Math.ceil(this.activeTotal / PAGE_SIZE)
    },
    totalResults() {
      return this.totals.matchTotal
    }
  },
  watch: {
    searchTerm() {
      if (!this.unified) {
        return
      }
      this.page = 0
      this.currentTab = 0
      if (this.searchTerm) {
        this.performSearch()
      }
    }
  },
  created() {
    // The shared search provider keeps its store reference; this is not component state.
    // eslint-disable-next-line vue/no-mutating-props
    this.dataProvider.store = Vue.store
    if (this.unified && !this.searching && this.searchTerm) {
      this.performSearch()
    }
  },
  methods: {
    useClassic() {
      this.$store.dispatch("setSearchExperience", false)
    },
    useUnified() {
      this.page = 0
      this.currentTab = 0
      this.$store.dispatch("setSearchExperience", true).then(() => {
        if (this.unified && this.searchTerm) {
          this.performSearch()
        }
      })
    },
    formatDate(date) {
      if (!date) {
        return ""
      }
      return Helpers.formatDate(date, "D MMM YYYY")
    },
    decorate(rows) {
      const self = this
      return (rows || []).map(function(row) {
        return self.decorateRow(row)
      })
    },
    decorateRow(row) {
      const decorated = Object.assign({}, row)
      const type = decorated.caseTypeId ? this.helpers.getCaseTypeById(this.$store.state, decorated.caseTypeId) : null
      if (type) {
        const displayName = type.displayName || type.name
        if (displayName) {
          decorated.caseTypeName = displayName
        }
        if (!decorated.caseTypeCode) {
          decorated.caseTypeCode = type.code
        }
        if (!decorated.iconSet) {
          decorated.iconSet = type.iconSet
        }
        if (!decorated.iconCode) {
          decorated.iconCode = type.iconCode
        }
      }
      if (!decorated.matchedFieldLabel) {
        decorated.matchedFieldLabel = fieldLabel(type, decorated.matchedField)
      }
      return decorated
    },
    goToPage(nextPage) {
      if (nextPage < 0 || nextPage >= this.pageCount) {
        return
      }
      this.page = nextPage
      this.performSearch()
    },
    clearSearchError() {
      this.searchError = ""
    },
    showSearchError(message) {
      this.searchError = message || "Search did not return a valid response."
      this.results = []
      this.documentSearch = { documents: [], total: 0 }
      this.matches = null
      this.caseMatches = null
      this.documentMatches = null
      this.matchTotal = null
      this.caseMatchTotal = null
      this.documentMatchTotal = null
      if (this.$store && this.$store.commit) {
        this.$store.commit("displayFlashMessage", { message: this.searchError, severity: "error" })
      }
    },
    async performSearch() {
      this.searching = true
      this.clearSearchError()
      if (this.searchTerm && this.searchTerm.length > 0) {
        const payload = await this.dataProvider.getGlobalSearch(this.searchTerm, this.page, PAGE_SIZE)
        if (!payload || payload.error) {
          this.showSearchError(payload && payload.message)
          this.searching = false
          return
        }
        const results = payload && payload.results ? payload.results : []
        if (results && results.length > 0) {
          results.forEach((result) => {
            const type = this.helpers.getCaseTypeById(this.$store.state, result.caseTypeId)
            result.caseType = type
            if (type && type.displayName) {
              result.displayName = type.displayName
            } else if (type && type.name) {
              result.displayName = type.name
            }
          })
          this.results = results
        } else {
          this.results = []
        }
        if (payload && payload.documents) {
          this.documentSearch = payload.documents
        } else {
          this.documentSearch = { documents: [], total: 0 }
        }
        if (payload && payload.matches) {
          this.matches = payload.matches
          this.caseMatches = payload.caseMatches || []
          this.documentMatches = payload.documentMatches || []
          this.matchTotal = payload.matchTotal || 0
          this.caseMatchTotal = payload.caseMatchTotal || 0
          this.documentMatchTotal = payload.documentMatchTotal || 0
        } else {
          this.matches = null
          this.caseMatches = null
          this.documentMatches = null
          this.matchTotal = null
          this.caseMatchTotal = null
          this.documentMatchTotal = null
        }
      }
      this.searching = false
    },
    fileFetchUrl(path) {
      const documentPath = path || ""
      if (window.location.href.indexOf("http://localhost") === 0) {
        return "http://localhost:6543" + documentPath
      }
      return documentPath
    },
    async openFile(item) {
      try {
        const path = item && item.url ? item.url : ""
        const url = this.fileFetchUrl(path)
        const auth = "Bearer " + window.localStorage.session
        const anchor = document.createElement("a")
        document.body.appendChild(anchor)
        const headers = new Headers()
        headers.append("Authorization", auth)
        const response = await fetch(url, { headers: headers })
        if (!response.ok) {
          throw new Error("document fetch failed")
        }
        const blobby = await response.blob()
        const objectUrl = window.URL.createObjectURL(blobby)
        anchor.href = objectUrl
        anchor.target = "blank"
        anchor.click()
      } catch (err) {
        this.$store.commit("displayFlashMessage", { message: "Failed to view/download file", severity: "error" })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.search-experience-root {
  height: 100%;
}

.search-results-page {
  margin-top: -60px;
  background: #fff;
  height: calc(100vh - 50px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: 20px 32px 32px;
}

.search-results-frame {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.search-results-header {
  flex: 0 0 auto;
  padding: 0;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 2;
}

.search-results-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.search-results-inset {
  padding: 12px 0 0;
}

.search-hit-summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 0 4px;
  color: rgba(0, 0, 0, 0.7);
  font-size: 14px;
}

.search-hit-summary-error {
  color: #b71c1c;
}

.search-hit-error {
  padding: 20px;
  color: #b71c1c;
  background: #fdecea;
  border-top: 1px solid rgba(183, 28, 28, 0.2);
}

.search-hit-tabs {
  max-width: 100%;
  margin-left: -12px;
}

.search-hit-tabs >>> .v-tabs__bar {
  background: transparent;
  box-shadow: none;
}

.search-hit-tab {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.search-hit-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 12px 12px;
  color: rgba(0, 0, 0, 0.54);
}
</style>
